import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { getErrorMessage } from '../../../../../ReactQuery/errors';
import useEditable from '../../../../../hooks/useEditable';
import useUpdateReleaseGroup from '../hooks/useUpdateReleaseGroup';
import type { ProjectGroup } from '../../../../../../shared/model-typings';
import type { UpdateProjectGroupRequest } from '../../../../../../typings/api';
import type { UseEditable } from '../../../../../hooks/useEditable';

type ReleaseGroupSettingsFields =
  | 'title'
  | 'policyId'
  | 'securityPolicyId'
  | 'qualityPolicyId'
  | 'publicOnPortal'
  | 'defaultSavedOptionLicensingReport'
  | 'defaultSavedOptionSbom';

export type ReleaseGroupSettings = Pick<ProjectGroup, ReleaseGroupSettingsFields>;

type ReleaseGroupSettingsProps = ReleaseGroupSettings & {
  projectGroupId: string; // String as it's coming from the query params
  reportCustomText: string | null;
  onRefetchAfterSave: () => Promise<void>;
};

//  This contains the state and handlers for the settings
type ReleaseGroupSettingsState = Omit<UseEditable<ReleaseGroupSettings>, 'onSave'>
  & Pick<ReleaseGroupSettingsProps, 'onRefetchAfterSave' | 'reportCustomText' | 'projectGroupId'>
  & { isSaving: boolean; onSave: () => Promise<void> };

const ReleaseGroupSettingsContext = createContext<ReleaseGroupSettingsState>({} as ReleaseGroupSettingsState);

export const useReleaseGroupsSettings = () => useContext(ReleaseGroupSettingsContext);

/**
 * This component reads the projectGroup which is passed to it from the parent component,
 * and sets up the state for the settings to be edited and saved.
 */
const ReleaseGroupSettingsContextProvider: FunctionComponent<ReleaseGroupSettingsProps> = ({
  children,
  projectGroupId,
  reportCustomText,
  title,
  policyId,
  securityPolicyId,
  qualityPolicyId,
  publicOnPortal,
  defaultSavedOptionLicensingReport,
  defaultSavedOptionSbom,
  onRefetchAfterSave,
}) => {
  const onAddToast = useAddToast();
  const onUpdateReleaseGroup = useUpdateReleaseGroup();

  //  We need to memoize this so that useEditable can determine if the settings have actually changed.
  const settings = useMemo(
    () => ({
      title,
      policyId,
      securityPolicyId,
      qualityPolicyId,
      publicOnPortal,
      defaultSavedOptionLicensingReport,
      defaultSavedOptionSbom,
    }),
    [
      title,
      policyId,
      securityPolicyId,
      qualityPolicyId,
      publicOnPortal,
      defaultSavedOptionLicensingReport,
      defaultSavedOptionSbom,
    ],
  );
  const { onSave: onSaveSettings, ...state } = useEditable<ReleaseGroupSettings>(settings);
  const [isSaving, setIsSaving] = useState(false);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const request: UpdateProjectGroupRequest = {
        title: state.data.title,
        licensingPolicyId: state.data.policyId, // Note this is named different in the request for whatever reason.
        securityPolicyId: state.data.securityPolicyId,
        qualityPolicyId: state.data.qualityPolicyId,
        defaultSavedOptionLicensingReport: state.data.defaultSavedOptionLicensingReport,
        defaultSavedOptionSbom: state.data.defaultSavedOptionSbom,
      };
      await onUpdateReleaseGroup(projectGroupId, request);

      //  We need to update Redux, so this will trigger a refetch of the project group
      //  which passes down the settings to this component. This isn't the most efficient,
      //  but it's the easiest way to keep the settings in sync.
      await onRefetchAfterSave();

      onSaveSettings();
      onAddToast({
        type: 'success',
        content: 'Settings saved successfully',
      });
    } catch (error) {
      onAddToast({
        type: 'error',
        content: getErrorMessage(error, 'Error saving settings'),
      });
    } finally {
      setIsSaving(false);
    }
  }, [onAddToast, onRefetchAfterSave, onSaveSettings, onUpdateReleaseGroup, projectGroupId, state.data]);

  const contextValue = useMemo(
    () => ({ ...state, onSave, isSaving, onRefetchAfterSave, reportCustomText, projectGroupId }),
    [state, onSave, isSaving, onRefetchAfterSave, reportCustomText, projectGroupId],
  );

  return (
    <ReleaseGroupSettingsContext.Provider value={contextValue}>
      {children}
    </ReleaseGroupSettingsContext.Provider>
  );
};

export default ReleaseGroupSettingsContextProvider;
